// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import dataActionCreators from "@modules/hierarchy/actionCreator";
import * as clientsSelectors from "@modules/client/selectors";
import { ModalTemplate } from "@components";
import schema from "./__schema__/schema";
import uiSchema from "./__ui__/schema";
import type { Client } from "@modules/client/types";
import AddModal from "./add-modal";
import DeletionModal from "./deletion-modal";
import MoveModal from "./move-modal";
import RenameModal from "./add-modal";
import RevertModal from "./revert-modal";
import SubmissionModal from "./submission-modal";

const validationRegExp = /^\s*$/;

type PropsFromState = {|
  client: Client,
|};

type PropsFromDispatch = {|
  addBranch: typeof dataActionCreators.addBranch,
  discardDraft: typeof dataActionCreators.discardDraft,
  moveBranch: typeof dataActionCreators.moveBranch,
  renameBranch: typeof dataActionCreators.renameBranch,
  revertChanges: typeof dataActionCreators.revertChanges,
  setHierarchyModal: typeof dataActionCreators.setHierarchyModal,
  submitDraftHierarchyTree: typeof dataActionCreators.submitDraftHierarchyTree,
|};

type OwnProps = {|
  clientId: string,
  showHierarchyModal: {
    action: string,
    id?: string,
  },
|};

type Props = OwnProps & PropsFromState & PropsFromDispatch & PropsFromLocalize;

export class HierarchyModal extends React.Component<Props> {
  addBranch = (e: Object) =>
    this.props.addBranch(
      Object.assign(
        {
          clientId: this.props.clientId,
          id: this.props.showHierarchyModal.id,
        },
        e.formData,
      ),
    );

  discardDraft = () => this.props.discardDraft({ clientId: this.props.clientId });

  onCancel = () => this.props.setHierarchyModal(null);

  moveBranch = (e: Object) =>
    this.props.moveBranch(
      Object.assign(
        {
          clientId: this.props.clientId,
          id: this.props.showHierarchyModal.id,
        },
        e.formData,
      ),
    );

  renameBranch = (e: Object) =>
    this.props.renameBranch(
      Object.assign(
        {
          clientId: this.props.clientId,
          id: this.props.showHierarchyModal.id,
        },
        e.formData,
      ),
    );

  revert = () =>
    this.props.revertChanges({
      clientId: this.props.clientId,
      id: this.props.showHierarchyModal.id,
    });

  submitDraftHierarchyTree = () =>
    this.props.submitDraftHierarchyTree({ clientId: this.props.clientId });

  validateAdd = (formData: Object, errors: Object) => {
    if (validationRegExp.test(formData.name)) {
      errors.name.addError(this.props.t("NON_SPACE_ERROR"));
    }
    if (validationRegExp.test(formData.organisationalUnitReference)) {
      errors.organisationalUnitReference.addError(this.props.t("NON_SPACE_ERROR"));
    }
    return errors;
  };

  validateRename = (formData: Object, errors: Object) => {
    if (validationRegExp.test(formData.newName)) {
      errors.newName.addError(this.props.t("NON_SPACE_ERROR"));
    }
    return errors;
  };

  get title() {
    return {
      add: "ADD_NEW_DEPARTMENT",
      confirmDeletion: "CANCEL_DRAFT",
      confirmSubmission: "SUBMIT_DRAFT",
      move: "MOVE_BRANCH",
      rename: "RENAME_BRANCH",
      revert: "REVERT_CHANGES",
    }[this.props.showHierarchyModal.action];
  }

  get modalBody() {
    switch (this.props.showHierarchyModal.action) {
      case "add":
        return (
          <AddModal
            onCancel={this.onCancel}
            onSubmit={this.addBranch}
            schema={schema.add}
            t={this.props.t}
            uiSchema={uiSchema.add}
            validate={this.validateAdd}
          />
        );
      case "confirmDeletion":
        return (
          <DeletionModal onCancel={this.onCancel} onSubmit={this.discardDraft} t={this.props.t} />
        );
      case "confirmSubmission":
        return (
          <SubmissionModal
            onCancel={this.onCancel}
            onSubmit={this.submitDraftHierarchyTree}
            t={this.props.t}
          />
        );
      case "move":
        return (
          <MoveModal
            onCancel={this.onCancel}
            onSubmit={this.renameBranch}
            schema={schema.rename}
            t={this.props.t}
            uiSchema={uiSchema.rename}
          />
        );
      case "rename":
        return (
          <RenameModal
            onCancel={this.onCancel}
            onSubmit={this.renameBranch}
            schema={schema.rename}
            t={this.props.t}
            uiSchema={uiSchema.rename}
            validate={this.validateRename}
          />
        );
      case "revert":
        return <RevertModal onCancel={this.onCancel} onSubmit={this.revert} t={this.props.t} />;
      default:
        return <div>{`Action received: '${JSON.stringify(this.props.showHierarchyModal)}'`}</div>;
    }
  }

  render() {
    return (
      <ModalTemplate
        className="modal-dialog modal-normal modal-dialog-centered"
        title={this.props.t(this.title)}
      >
        {this.modalBody}
      </ModalTemplate>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    client: clientsSelectors.getClient(state),
  };
};

const mapDispatchToProps = {
  addBranch: dataActionCreators.addBranch,
  discardDraft: dataActionCreators.discardDraft,
  moveBranch: dataActionCreators.moveBranch,
  renameBranch: dataActionCreators.renameBranch,
  revertChanges: dataActionCreators.revertChanges,
  setHierarchyModal: dataActionCreators.setHierarchyModal,
  submitDraftHierarchyTree: dataActionCreators.submitDraftHierarchyTree,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(HierarchyModal));
